import { useEffect, useState } from 'react';

// Probe-on-failure device liveness for the remote-view popups (VNC /
// Moonlight). While `active`, asks the backend to TCP-dial the device every
// `intervalMs` and reports the verdict — a live dial over the tailnet, so it
// answers in seconds where the polled Tailscale status can lag a device drop
// by minutes. Callers only activate it once a session stalls or the polled
// status says offline, so a healthy session costs zero probes.
//
// Port 22 by default: sshd tolerates probe connections, unlike the
// single-threaded x11vnc on 5900, which reconnect storms can wedge.
//
// Returns null (unknown: not probing, or no verdict yet), true, or false.
export default function useHostReachability(
	host,
	active,
	{ port = 22, intervalMs = 10000 } = {}
) {
	const [reachable, setReachable] = useState(null);

	useEffect(() => {
		if (!active || !host) {
			setReachable(null);
			return undefined;
		}

		let cancelled = false;

		const probe = async () => {
			try {
				const res = await fetch(
					`/api/host-reachability?host=${encodeURIComponent(host)}&port=${port}&timeout=3`,
					{ method: 'GET', credentials: 'include' }
				);
				if (!res.ok) return; // auth/backend problem — not a verdict on the device
				const data = await res.json().catch(() => null);
				if (!cancelled && typeof data?.reachable === 'boolean') {
					setReachable(data.reachable);
				}
			} catch {
				// couldn't reach our own backend — keep the last verdict
			}
		};

		probe();
		const id = setInterval(probe, intervalMs);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, [host, active, port, intervalMs]);

	return reachable;
}
